/**
 * Gevelcalculator Compolux Highline — rekenkern.
 *
 * Puur rekenwerk, geen DOM. Draait in de browser (window.AdeqoGevelCore) en in
 * node (module.exports), zodat de referentiecases uit blog #34 en #35 in een
 * test na te rekenen zijn zonder browser.
 *
 * De methode is exact de methode die op adeqo.eu gepubliceerd staat in
 * "gevelbekleding berekenen" (#35) en "kosten composiet gevelbekleding" (#34).
 * Een bezoeker die de blog naleest moet op hetzelfde getal uitkomen; daarom
 * rekenen we met de gepubliceerde, afgeronde paneelmaten 0,71 en 0,61 m² en
 * niet met lengte × 0,2025.
 *
 * NIET overgenomen uit het partner-prijsmodel (tabblad Offerte Calculator),
 * met opzet — zie CONSTANTS-commentaar en docs/gevelcalculator.md:
 *   1. verticale-montage paneelformule       → oppervlakmethode, horizontaal
 *   2. balken via paneellengte, 9 hardcoded  → posities × hoogte / balklengte
 *   3. startclip per paneel                  → startclip alleen onderste rij
 *   4. transport €95 / gratis boven €1.500   → Adeqo-drempel, buiten de kern
 */
(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.AdeqoGevelCore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var C = {
    // Werkende breedte, niet de plankbreedte van 22,1 cm. Dit is de fout die
    // blog #35 expliciet benoemt: reken je met 22,1 dan kom je planken tekort.
    WERKENDE_BREEDTE_M: 0.2025,
    PLANKBREEDTE_M: 0.221,

    // Gepubliceerde dekking per paneel (lengte × werkende breedte, afgerond).
    //
    // NIET exact maken. De echte dekking is 0,6075 en 0,70875, maar 0,61 en 0,71
    // staan zo in blog #34 en #35 en de UI toont dit getal op twee decimalen aan
    // de klant (adeqo-gevelcalculator.js r.364). Reken je met de exacte waarde,
    // dan klopt die zichtbare rekenstap niet meer ("gedeeld door 0,61 ... 19,0")
    // en valt het advies in 61 procent van de gevelmaten een paneel hoger uit.
    // Wie hem toch omzet, past #34 (62 panelen) en #35 (20,1 en 18,2) mee aan.
    PANEEL_M2: { 300: 0.61, 350: 0.71 },

    ZAAGVERLIES: 1.10,      // standaard 10%
    ZAAGVERLIES_ZWAAR: 1.15, // meer dan 4 buitenhoeken
    HOEKEN_DREMPEL: 4,

    // Regelwerk: h.o.h. 35 cm (besluit Sam 15-10-2026; montagegids Compolux: max 350 mm).
    HOH_REGEL_M: 0.35,
    BALK_LENGTE_M: 2.8,

    // Clips volgen de productteksten van de clips (besluit Sam 07-10-2026):
    // "Reken 7 à 8 montageclips per paneel" (we nemen 8, de teksten ronden zelf
    // naar boven af) en "1 startclip per paneel, en 2 bij het eerste paneel".
    // Geen reserve erbovenop: 21 panelen geeft 168 montageclips (4 zakjes van 50)
    // en 22 startclips (2 zakjes van 15), het voorbeeld uit die teksten.
    MONTAGECLIPS_PER_PANEEL: 8,
    STARTCLIPS_EXTRA: 1,
    // Zakinhoud zoals adeqo.eu de clips verkoopt, bevestigd door Sam 07-10-2026:
    // montageclips (CL09055-0593) per 50, startclips (CL09055-0493) per 15, net
    // als in de producttitels. Dat vervangt de 250/100 van 18-08. Slagpluggen
    // zijn op 07-10-2026 uit de calculator gehaald: Adeqo heeft ze niet en krijgt
    // ze niet (Sam).
    VERPAKKING: { montageclips: 50, startclips: 15 },

    PANEELLENGTES_CM: [350, 300] // aflopend: bij gelijk overschot wint de langste
  };

  /**
   * Naar boven afronden met een epsilon-marge. Zonder die marge tikt
   * drijvende-komma-ruis een exacte uitkomst een heel paneel omhoog:
   * 98 / 2,8 wordt in JS 34,99999999999999 en 35,0 kan 35,00000000000001
   * worden. Beide moeten 35 opleveren, niet 34 of 36.
   */
  function ceilSafe(x) {
    return Math.ceil(x - 1e-9);
  }

  /** Naar beneden afronden met dezelfde marge, zodat een exacte 8 geen 7 wordt. */
  function floorSafe(x) {
    return Math.floor(x + 1e-9);
  }

  function num(v, fallback) {
    var n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
    return isFinite(n) ? n : (fallback || 0);
  }

  /** Netto m² van één vlak: breedte × hoogte minus alle openingen erin. */
  function vlakOppervlak(vlak) {
    var bruto = num(vlak.breedte) * num(vlak.hoogte);
    var openingen = (vlak.openingen || []).reduce(function (som, o) {
      var aantal = Math.max(0, Math.round(num(o.aantal, 1)));
      return som + aantal * num(o.breedte) * num(o.hoogte);
    }, 0);
    return { bruto: bruto, openingen: openingen, netto: bruto - openingen };
  }

  /** Aantal kozijnen over alle vlakken — elk kozijn vraagt een extra regel. */
  function telKozijnen(vlakken) {
    return vlakken.reduce(function (som, v) {
      return som + (v.openingen || []).reduce(function (s, o) {
        return s + Math.max(0, Math.round(num(o.aantal, 1)));
      }, 0);
    }, 0);
  }

  /**
   * Paneellengte-advies.
   *
   * Het Highline-profiel gaat uitsluitend horizontaal, dus een plank loopt
   * altijd langs de breedte van een vlak en de hoogte bepaalt alleen het aantal
   * rijen. De vraag is daarmee: welke van de twee lengtes laat over de hele
   * gevel het minste materiaal liggen.
   *
   * Per vlak passen er CEIL(breedte / lengte) planken naast elkaar; wat daarvan
   * overschiet is (dat aantal × lengte) − breedte. Dat overschot valt in elke
   * rij opnieuw, dus het weegt mee met het aantal rijen. Bij een gelijke stand
   * wint de langste plank, want die geeft minder stuiknaden per rij.
   *
   * De oude regel keek alleen of het breedste vlak boven de 3 meter uitkwam.
   * Die adviseerde 350 cm bij een vlak van precies 6,00 m, terwijl daar twee
   * planken van 300 cm exact in passen en er van de 350 cm per rij een meter
   * overblijft.
   *
   * Dit bepaalt uitsluitend WELKE lengte wordt voorgesteld. Het aantal panelen
   * blijft uit de oppervlaktemethode van blog #34 en #35 komen.
   */
  function adviesPaneellengte(vlakken) {
    var beste = null;

    C.PANEELLENGTES_CM.forEach(function (cm) {
      var lengteM = cm / 100;
      var overschot = 0;

      (vlakken || []).forEach(function (v) {
        var breedte = num(v.breedte);
        var hoogte = num(v.hoogte);
        if (breedte <= 0 || hoogte <= 0) return;
        var rijen = ceilSafe(hoogte / C.WERKENDE_BREEDTE_M);
        var perRij = ceilSafe(breedte / lengteM) * lengteM - breedte;
        overschot += rijen * Math.max(0, perRij);
      });

      // Strikt kleiner, zodat de eerste (langste) lengte een gelijkspel wint.
      if (!beste || overschot < beste.overschot - 1e-9) {
        beste = { cm: cm, overschot: overschot };
      }
    });

    return beste ? beste.cm : C.PANEELLENGTES_CM[0];
  }

  /**
   * @param {Object} input
   * @param {Array}  input.vlakken       [{breedte, hoogte, openingen:[{aantal,breedte,hoogte}]}] in meters
   * @param {number|string} input.paneellengte  300 | 350 | 'advies'
   * @param {string} input.kleur         'eiken' | 'teak' | 'zwart'
   * @param {number} input.buitenhoeken  aantal hoekprofielen
   * @param {number} input.eindafwerkingen aantal eindprofielen
   */
  function bereken(input) {
    var vlakken = (input.vlakken || []).filter(function (v) {
      return num(v.breedte) > 0 && num(v.hoogte) > 0;
    });

    var perVlak = vlakken.map(vlakOppervlak);
    var brutoVlak  = perVlak.reduce(function (s, v) { return s + v.bruto; }, 0);
    var openingen  = perVlak.reduce(function (s, v) { return s + v.openingen; }, 0);
    var nettoM2    = perVlak.reduce(function (s, v) { return s + v.netto; }, 0);

    if (nettoM2 <= 0) return null;

    var buitenhoeken = Math.max(0, Math.round(num(input.buitenhoeken, 0)));
    var zwaar        = buitenhoeken > C.HOEKEN_DREMPEL;
    var factor       = zwaar ? C.ZAAGVERLIES_ZWAAR : C.ZAAGVERLIES;
    var brutoM2      = nettoM2 * factor;

    var lengte = input.paneellengte === 'advies' || !input.paneellengte
      ? adviesPaneellengte(vlakken)
      : parseInt(input.paneellengte, 10);
    if (lengte !== 300 && lengte !== 350) lengte = adviesPaneellengte(vlakken);

    var paneelM2 = C.PANEEL_M2[lengte];
    var panelenRuw = brutoM2 / paneelM2;
    var panelen = ceilSafe(panelenRuw);

    // ---- Regelwerk. Panelen horizontaal betekent regels verticaal. ----
    var kozijnen = telKozijnen(vlakken);
    var posities = 0;
    var regelmeters = 0;
    vlakken.forEach(function (v) {
      var p = ceilSafe(num(v.breedte) / C.HOH_REGEL_M) + 1;
      p += (v.openingen || []).reduce(function (s, o) {
        return s + Math.max(0, Math.round(num(o.aantal, 1)));
      }, 0);
      posities += p;
      regelmeters += p * num(v.hoogte);
    });
    var balken = ceilSafe(regelmeters / C.BALK_LENGTE_M);

    // ---- Clips. ----
    // Clips rekenen per paneel, zoals de productteksten van de clips (zie
    // MONTAGECLIPS_PER_PANEEL). Tot 07-10-2026 rekende de kern met elke kruising
    // van paneel en regel (9 bij 300 cm, 10 bij 350 cm) plus 3% reserve; dat gaf
    // 217 clips waar de producttekst 170 noemt. Sam koos de productteksten.
    var clipsPerPaneel = C.MONTAGECLIPS_PER_PANEEL;
    var montageclips = panelen * clipsPerPaneel;
    var startclips   = panelen + C.STARTCLIPS_EXTRA;

    return {
      invoer: {
        vlakken: vlakken.length,
        openingen: kozijnen,
        paneellengte: lengte,
        paneellengteGeadviseerd: input.paneellengte === 'advies' || !input.paneellengte,
        kleur: input.kleur || 'eiken',
        buitenhoeken: buitenhoeken,
        eindafwerkingen: Math.max(0, Math.round(num(input.eindafwerkingen, 0)))
      },
      oppervlak: {
        bruto: brutoVlak,
        openingen: openingen,
        netto: nettoM2,
        zaagverliesFactor: factor,
        inclusiefZaagverlies: brutoM2
      },
      paneelM2: paneelM2,
      panelenRuw: panelenRuw,
      posities: posities,
      regelmeters: regelmeters,
      clipsPerPaneel: clipsPerPaneel,
      // stuks = losse eenheden, verpakkingen = wat er besteld wordt
      stuks: {
        panelen: panelen,
        hoekprofielen: buitenhoeken,
        eindprofielen: Math.max(0, Math.round(num(input.eindafwerkingen, 0))),
        onderbalken: balken,
        montageclips: montageclips,
        startclips: startclips
      },
      verpakkingen: {
        montageclips: ceilSafe(montageclips / C.VERPAKKING.montageclips),
        startclips: ceilSafe(startclips / C.VERPAKKING.startclips)
      }
    };
  }

  return {
    CONSTANTS: C,
    bereken: bereken,
    adviesPaneellengte: adviesPaneellengte,
    ceilSafe: ceilSafe,
    floorSafe: floorSafe
  };
});
